import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import ComingSoonPopup from '../ComingSoonPopup';
import { IconChat, IconPlay } from '../Icons';
import { landingMedia } from './media';

export function CtaSection({ onStart }: { onStart: () => void }) {
  const { t } = useTranslation('landing');
  return (
    <section id="start" className="section" style={{ paddingTop: 120, scrollMarginTop: 'var(--header-h)' }}>
      <div className="cta-panel">
        <span className="label accent" style={{ fontSize: 'var(--fs-label)', letterSpacing: 'var(--tr-label-wide)' }}>
          {t('cta.eyebrow', 'Free while we’re in beta')}
        </span>
        <h2
          className="t-h2"
          style={{ fontSize: 'clamp(30px, 4.8vw, 62px)', letterSpacing: 'var(--tr-hero)', margin: '18px auto 20px', maxWidth: 880 }}
        >
          {t('cta.titleA', 'The game you’ve been thinking about')}{' '}
          <span className="t-accent">{t('cta.titleB', 'could be your next release.')}</span>
        </h2>
        <p style={{ fontSize: 'var(--fs-body-lg)', lineHeight: 1.6, color: 'var(--text-4)', maxWidth: 540, margin: '0 auto 32px' }}>
          {t('cta.lede', 'Start with a sentence. Build a game you can launch, learn from and keep improving.')}
        </p>
        <div style={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: 12 }}>
          <button type="button" className="btn primary lg" style={{ padding: '0 32px' }} onClick={onStart}>
            {t('cta.startCreating', 'Start creating')}
          </button>
          <a href="#demo" className="btn lg" style={{ padding: '0 32px' }}>
            <IconPlay size={13} />
            {t('cta.watchDemo', 'Watch the demo')}
          </a>
        </div>
        <p className="t-mono" style={{ fontSize: 11.5, letterSpacing: 'var(--tr-label-tight)', color: 'var(--text-meta)', margin: '24px 0 0' }}>
          {t('cta.note', 'Start free. No credit card. Free during beta.')}
        </p>
      </div>
    </section>
  );
}

export function CommunitySection() {
  const { t } = useTranslation('landing');
  const [showComingSoon, setShowComingSoon] = useState(false);
  return (
    <section
      id="community"
      className="section"
      style={{
        paddingTop: 'var(--section-y-sm)',
        scrollMarginTop: 'var(--header-h)',
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(min(300px, 100%), 1fr))',
        gap: 20,
      }}
    >
      <div
        className="card quiet"
        style={{ padding: 32, display: 'flex', flexDirection: 'column', gap: 22, justifyContent: 'space-between' }}
      >
        <div>
          <span style={{ display: 'inline-flex', color: 'var(--accent)', marginBottom: 16 }}>
            <IconChat size={24} />
          </span>
          <h3 className="t-h3-lg" style={{ margin: '0 0 11px' }}>
            {t('community.title', 'Create with people who get it.')}
          </h3>
          <p className="t-body" style={{ color: 'var(--text-meta)', margin: 0, maxWidth: 420 }}>
            {t(
              'community.body',
              'Share builds, exchange feedback, discover new ideas and see what other creators are making with Entire.',
            )}
          </p>
        </div>
        <button type="button" className="btn" style={{ alignSelf: 'flex-start', padding: '0 24px' }} onClick={() => setShowComingSoon(true)}>
          {t('community.join', 'Join the community')}
        </button>
      </div>
      <div className="frame" style={{ borderRadius: 15 }}>
        <div className="frame-inner" style={{ borderRadius: 14, minHeight: 260, height: '100%' }}>
          <img src={landingMedia.community} alt={t('community.alt', 'Creators gathered in a circle')} loading="lazy" />
        </div>
      </div>
      <ComingSoonPopup isOpen={showComingSoon} onClose={() => setShowComingSoon(false)} title={t('community.join', 'Join the community')} />
    </section>
  );
}

export function FaqSection() {
  const { t } = useTranslation('landing');
  const items: Array<[string, string]> = [
    [
      t('faq.code.q', 'Do I need to know how to code?'),
      t(
        'faq.code.a',
        'No. Start by describing the game you want. Entire handles the code, assets and connected game infrastructure behind the scenes so you can focus on creating and refining the game.',
      ),
    ],
    [
      t('faq.change.q', 'Can I keep changing my game?'),
      t('faq.change.a', 'Yes. Keep giving Entire new instructions to change gameplay, content, visuals or balance and build the next playable version.'),
    ],
    [
      t('faq.publish.q', 'Can I publish my game?'),
      t('faq.publish.a', 'Yes. Entire is built to publish the same game project to Google Play, the App Store and Steam.'),
    ],
    [
      t('faq.money.q', 'Can I make money from my game?'),
      t(
        'faq.money.a',
        'Yes. Entire is being built to support monetization directly inside your game, including in-app purchases and ads. Soon, you’ll be able to add revenue systems without wiring together separate tools, so you can focus on improving the game while Entire helps you turn player engagement into revenue.',
      ),
    ],
    [
      t('faq.after.q', 'What happens after I publish?'),
      t(
        'faq.after.a',
        'Your game stays connected to Entire. Analytics, logs and player data from the live game flow back into the platform, where agents analyze performance and help identify and build the next improvements.',
      ),
    ],
    [
      t('faq.native.q', 'Is my game a web app or a real native game?'),
      t(
        'faq.native.a',
        'It’s a real native game. Entire builds games in C#, so they run natively on mobile and PC rather than as browser-based web apps.',
      ),
    ],
  ];
  return (
    <section
      style={{
        padding: 'var(--section-y) var(--gutter-inner) 0',
        maxWidth: 'calc(var(--container-prose) + 2 * var(--gutter-inner))',
        margin: '0 auto',
      }}
    >
      <h2 className="t-h2-xs" style={{ margin: '0 0 24px' }}>
        {t('faq.title', 'Questions')}
      </h2>
      {items.map(([q, a]) => (
        <details key={q} className="faq">
          <summary>{q}</summary>
          <p>{a}</p>
        </details>
      ))}
    </section>
  );
}

export function LandingFooter() {
  const { t } = useTranslation(['landing', 'common']);
  return (
    <footer className="site-footer" style={{ marginTop: 100 }}>
      <span className="wordmark" style={{ fontSize: 16 }}>
        <img src={landingMedia.logo} alt="" style={{ width: 20, height: 20 }} />
        {t('common:app.name', 'Entire')}
      </span>
      <span className="label faint" style={{ fontSize: 11, letterSpacing: '0.14em' }}>
        {t('landing:footer.tagline', 'Build your game. Launch it. Grow it.')}
      </span>
    </footer>
  );
}
